// TelcoTrack — provozní město ve generaci smluv = pobočky firmy.
// Hodnota odeslaná do generátoru zůstává `HrCityId` (`hrCityCode` pobočky).

import {
  HR_CITY_IDS,
  HR_CITY_LABELS,
  hrCitiesForCompany,
  isHrCityId,
  isHrCompanyId,
  type HrCityId,
  type HrCompanyId,
} from '../constants/hr-onboarding.constants';
import { POLYGON_RESPONSIBLE_BRANCHES } from '../constants/polygon-business.constants';
import type { TenantBranchDto, TenantOrgConfigDto } from '../interfaces/org-config.interface';
import { branchFromHrIds } from './user-directory-profile.util';
import {
  resolveTenantCompanyId,
  type TenantCompanyMatchRef,
} from './tenant-company-match.util';

export interface HrContractCityOption {
  /** Id pobočky, nebo kanonický název když katalog ještě není. */
  key: string;
  /** Název pobočky. */
  label: string;
  /** `cityId`, které generátor smluv už očekává. */
  cityId: HrCityId;
}

export interface HrContractCityOptionInput {
  catalog?: TenantOrgConfigDto | null;
  /** HR kód zaměstnavatele (`protelco` / `fiber_beta` / `extermis`). */
  hrCompanyId?: string | null;
  /** UUID vybrané firmy (pozvánka / zámek). */
  organizationId?: string | null;
  /** Firmy z pickeru, když katalog ještě nedorazil. */
  companies?: readonly { id: string; code?: string | null; legalName?: string | null }[];
}

function hrCompany(value: string | null | undefined): HrCompanyId | '' {
  const code = String(value ?? '').trim().toLowerCase();
  return isHrCompanyId(code) ? code : '';
}

function companyCatalog(
  catalog: TenantOrgConfigDto | null | undefined,
  extra: HrContractCityOptionInput['companies'],
): TenantCompanyMatchRef[] {
  const byId = new Map<string, TenantCompanyMatchRef>();
  for (const company of extra ?? []) {
    const id = String(company.id ?? '').trim();
    if (!id) continue;
    byId.set(id, {
      id,
      code: String(company.code ?? '').trim(),
      legalName: String(company.legalName ?? '').trim(),
    });
  }
  for (const company of catalog?.companies ?? []) {
    if (company.isActive === false) continue;
    const id = company.id.trim();
    if (!id) continue;
    byId.set(id, {
      id,
      code: company.code,
      legalName: company.legalName,
      isActive: company.isActive,
    });
  }
  return [...byId.values()];
}

/** Firma, jejíž pobočky patří do filtru. Smluvní HR kód má přednost před cizí organizací. */
function scopeCompanyId(
  companies: readonly TenantCompanyMatchRef[],
  input: HrContractCityOptionInput,
  hr: HrCompanyId | '',
): string | null {
  const known = (id: string | null): string | null =>
    id && companies.some((company) => company.id === id) ? id : null;
  if (hr) {
    const byCode = known(resolveTenantCompanyId(companies, { companyCode: hr }));
    if (byCode) return byCode;
  }
  const organizationId = String(input.organizationId ?? '').trim();
  if (!organizationId) return null;
  return known(resolveTenantCompanyId(companies, { companyId: organizationId }));
}

function cityIdForBranch(
  branch: Pick<TenantBranchDto, 'code' | 'name' | 'hrCityCode'>,
  hr: HrCompanyId | '',
): HrCityId | null {
  const allowed: readonly HrCityId[] = hr ? hrCitiesForCompany(hr) : HR_CITY_IDS;
  const tokens = [branch.hrCityCode, branch.code]
    .map((value) => String(value ?? '').trim().toLowerCase())
    .filter(Boolean);
  for (const token of tokens) {
    if (isHrCityId(token) && allowed.includes(token)) return token;
  }
  const name = branch.name.trim().toLowerCase();
  if (hr && name) {
    for (const city of allowed) {
      const canonical = branchFromHrIds(hr, city);
      if (canonical && canonical.toLowerCase() === name) return city;
    }
  }
  if (name) {
    for (const city of allowed) {
      if (name.includes(HR_CITY_LABELS[city].toLowerCase())) return city;
    }
  }
  return allowed.length === 1 ? allowed[0] : null;
}

function fromBranches(
  branches: readonly TenantBranchDto[],
  hr: HrCompanyId | '',
): HrContractCityOption[] {
  return [...branches]
    .filter((branch) => branch.isActive !== false)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'cs'))
    .flatMap((branch) => {
      const label = branch.name.trim();
      const cityId = cityIdForBranch(branch, hr);
      if (!label || !cityId) return [];
      return [{ key: branch.id, label, cityId }];
    });
}

/** Kanonické pobočky polygonů / adresáře, jen pro zvolenou HR firmu. */
function canonicalForHrCompany(hr: HrCompanyId): HrContractCityOption[] {
  const out: HrContractCityOption[] = [];
  for (const name of POLYGON_RESPONSIBLE_BRANCHES) {
    const cityId = hrCitiesForCompany(hr).find((city) => branchFromHrIds(hr, city) === name);
    if (!cityId) continue;
    out.push({ key: name, label: name, cityId });
  }
  return out;
}

/**
 * Volby filtru Provozní město: pobočky zvolené firmy (stejný katalog jako Firmy).
 * Když katalog pobočky nemá, stejný seznam jako Zodpovědná pobočka (`POLYGON_RESPONSIBLE_BRANCHES`).
 */
export function hrContractCityOptions(input: HrContractCityOptionInput): HrContractCityOption[] {
  const hr = hrCompany(input.hrCompanyId);
  const companies = companyCatalog(input.catalog, input.companies);
  const companyId = scopeCompanyId(companies, input, hr);
  const active = (input.catalog?.branches ?? []).filter((branch) => branch.isActive !== false);
  if (companyId && active.length > 0) {
    const own = fromBranches(
      active.filter((branch) => branch.companyId.trim() === companyId.trim()),
      hr,
    );
    if (own.length > 0) return own;
  }
  if (!hr) return [];
  return canonicalForHrCompany(hr);
}
